import React, { useState } from "react";
import { Music, X, Plus } from "lucide-react";
import { adminAssignSongToUser } from "./userApi";
import styles from "./UserShared.module.css";

interface AssignSongModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSongAssigned: () => void;
  userId: string;
  assignedSongIds: string[];
}

const AssignSongModal: React.FC<AssignSongModalProps> = ({
  isOpen,
  onClose,
  onSongAssigned,
  userId,
  assignedSongIds,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [songId, setSongId] = useState("");

  const resetForm = () => {
    setSongId("");
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!songId.trim()) {
      setError("Lütfen şarkı ID'si girin");
      return;
    }

    if (assignedSongIds.includes(songId.trim())) {
      setError("Bu şarkı zaten kullanıcıya atanmış");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Admin endpoint'ini kullan - kullanıcı ID'si ile birlikte
      await adminAssignSongToUser(userId, songId.trim());

      setSuccess("✅ Şarkı başarıyla kullanıcıya atandı!");

      // 1.5 saniye bekleyip modal'ı kapat
      setTimeout(() => {
        onSongAssigned();
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error("Song assignment error:", err);

      // API hata mesajlarını daha anlaşılır hale getir
      let errorMessage = "Şarkı atanırken hata oluştu";

      if (err.response?.status === 400) {
        errorMessage = "Geçersiz şarkı ID'si. Lütfen doğru bir ID girin.";
      } else if (err.response?.status === 403) {
        errorMessage = "Bu işlemi gerçekleştirmek için yetkiniz yok.";
      } else if (err.response?.status === 404) {
        errorMessage =
          "Şarkı bulunamadı. Lütfen geçerli bir şarkı ID'si girin.";
      } else if (err.response?.status === 409) {
        errorMessage = "Bu şarkı zaten kullanıcıya atanmış.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} style={{ maxWidth: "500px" }}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <Music size={20} />
            Şarkı Ata
          </h2>
          <button onClick={handleClose} className={styles.modalCloseButton}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {error && (
            <div className={styles.errorAlert}>
              <X size={16} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className={styles.successAlert}>
              <span>{success}</span>
            </div>
          )}

          <div className={styles.formSection}>
            <div className={styles.inputGroup}>
              <label htmlFor="songId" className={styles.label}>
                <Music size={16} />
                Şarkı ID *
              </label>
              <input
                id="songId"
                type="text"
                value={songId}
                onChange={(e) => setSongId(e.target.value)}
                className={styles.input}
                placeholder="Şarkı ID'sini girin (örn: 507f1f77bcf86cd799439011)"
                required
              />
              <p className={styles.inputHelper}>
                Atamak istediğiniz şarkının benzersiz ID'sini girin
              </p>
            </div>

            {assignedSongIds.length > 0 && (
              <div className={styles.inputGroup}>
                <label className={styles.label}>Zaten Atanmış Şarkılar</label>
                <div className={styles.tagGrid}>
                  {assignedSongIds.map((id, index) => (
                    <span key={index} className={styles.tag}>
                      <Music size={12} />
                      {id}
                    </span>
                  ))}
                </div>
                <p className={styles.inputHelper}>
                  Bu şarkılar zaten kullanıcıya atanmış
                </p>
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={handleClose}
              className={styles.cancelButton}
              disabled={loading}
            >
              <X size={16} />
              İptal
            </button>

            <button
              type="submit"
              className={styles.saveButton}
              disabled={loading || !songId.trim()}
            >
              {loading ? (
                <>
                  <div className={styles.spinner}></div>
                  Atanıyor...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Şarkıyı Ata
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignSongModal;
